/**
 * Звук заставки на Web Audio: музыкальный отрывок + синтезированные
 * гром, дождь, ветер, скрип ворот, карканье и удар под логотип.
 * Синтез не весит ничего в сети и даёт точную привязку к кадрам:
 * часы заставки — это часы AudioContext.
 *
 * Работает и с OfflineAudioContext — так собирается звуковая дорожка
 * для обзорного ролика (см. tools/intro/capture.mjs).
 */
import musicUrl from "./assets/intro_music.mp3";
import { CUE, STRIKES } from "./timeline";
import { rng } from "./pixels";

const LS_SOUND = "tipaduma.audio.sound";
const LS_MUSIC = "tipaduma.audio.music";

function readVol(key: string, def: number): number {
  try {
    const v = parseFloat(localStorage.getItem(key) ?? "");
    return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : def;
  } catch {
    return def;
  }
}

// ─── синтез буферов ───────────────────────────────────────────────────────

function onePoleCoef(fc: number, sr: number): number {
  return 1 - Math.exp((-2 * Math.PI * fc) / sr);
}

interface ThunderOpts {
  dur: number;
  crack: number; // 0..1 — сухой треск в начале (близкий удар)
  lp: number; // срез НЧ раската
  delay: number; // запаздывание раската (далёкий гром)
  seed: number;
  gain: number;
}

function thunder(ctx: BaseAudioContext, o: ThunderOpts): AudioBuffer {
  const sr = ctx.sampleRate;
  const n = Math.round(o.dur * sr);
  const buf = ctx.createBuffer(2, n, sr);
  const r = rng(o.seed);
  // Раскаты: несколько волн с разной задержкой и длиной.
  const rolls: Array<[number, number, number]> = [[o.delay, 1, 0.7 + r() * 0.4]];
  for (let k = 0; k < 4; k++) rolls.push([o.delay + 0.15 + r() * 1.6, 0.2 + r() * 0.3, 0.6 + r() * 1.0]);
  const crackles: Array<[number, number]> = [];
  for (let k = 0; k < 22; k++) crackles.push([r() * 0.45, 0.2 + r() * 0.8]);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    const rr = rng(o.seed * 7 + ch * 1013);
    let brown = 0;
    let lp1 = 0;
    let lp2 = 0;
    let hpPrev = 0;
    let hpOut = 0;
    const a = onePoleCoef(o.lp, sr);
    const hpA = Math.exp((-2 * Math.PI * 1400) / sr);
    let peak = 0;
    for (let i = 0; i < n; i++) {
      const t = i / sr;
      const white = rr() * 2 - 1;
      brown = (brown + 0.04 * white) * 0.996;
      lp1 += a * (brown * 6 - lp1);
      lp2 += a * (lp1 - lp2);
      let env = 0;
      for (const [t0, amp, dec] of rolls) {
        if (t < t0) continue;
        const u = t - t0;
        env += amp * (1 - Math.exp(-u / 0.035)) * Math.exp(-u / dec);
      }
      // «перекатывание»: медленная модуляция громкости
      env *= 0.75 + 0.25 * Math.sin(t * 7.3 + ch) * Math.sin(t * 3.1);
      let v = lp2 * env;
      if (o.crack > 0) {
        hpOut = hpA * (hpOut + white - hpPrev);
        hpPrev = white;
        let ce = Math.exp(-t / 0.045) * 1.4;
        for (const [ct, ca] of crackles) {
          const u = t - ct;
          if (u >= 0 && u < 0.03) ce += ca * Math.exp(-u / 0.006);
        }
        v += hpOut * ce * o.crack;
      }
      d[i] = v;
      peak = Math.max(peak, Math.abs(v));
    }
    const norm = (0.95 * o.gain) / Math.max(1e-6, peak);
    for (let i = 0; i < n; i++) d[i] *= norm;
  }
  return buf;
}

function noiseLoop(ctx: BaseAudioContext, dur: number, seed: number, kind: "rain" | "wind"): AudioBuffer {
  const sr = ctx.sampleRate;
  const fade = Math.round(0.5 * sr);
  const n = Math.round(dur * sr);
  const buf = ctx.createBuffer(2, n, sr);
  for (let ch = 0; ch < 2; ch++) {
    const r = rng(seed + ch * 31);
    const tmp = new Float32Array(n + fade);
    let lp = 0;
    let lp2 = 0;
    let hpPrev = 0;
    let hp = 0;
    let brown = 0;
    const aLp = onePoleCoef(kind === "rain" ? 5200 : 380, sr);
    const hpA = Math.exp((-2 * Math.PI * (kind === "rain" ? 500 : 40)) / sr);
    let drop = 0;
    let dropF = 0;
    let dropPh = 0;
    for (let i = 0; i < tmp.length; i++) {
      const t = i / sr;
      const w = r() * 2 - 1;
      if (kind === "rain") {
        hp = hpA * (hp + w - hpPrev);
        hpPrev = w;
        lp += aLp * (hp - lp);
        // отдельные капли: короткие «тики» по жести и листьям
        if (r() < 45 / sr) {
          drop = 0.15 + r() * 0.5;
          dropF = 1800 + r() * 2600;
          dropPh = 0;
        }
        drop *= 0.9985;
        dropPh += (2 * Math.PI * dropF) / sr;
        tmp[i] = lp * 0.55 + Math.sin(dropPh) * drop * 0.12;
      } else {
        brown = (brown + 0.03 * w) * 0.997;
        lp += aLp * (brown * 5 - lp);
        lp2 += aLp * (lp - lp2);
        const am = 0.55 + 0.3 * Math.sin(t * 0.7 + ch) + 0.15 * Math.sin(t * 1.9);
        tmp[i] = lp2 * am;
      }
    }
    // бесшовная петля: хвост подмешиваем в начало
    const d = buf.getChannelData(ch);
    for (let i = 0; i < n; i++) d[i] = tmp[i];
    for (let i = 0; i < fade; i++) {
      const k = i / fade;
      d[i] = tmp[i] * k + tmp[n + i] * (1 - k);
    }
    let peak = 0;
    for (let i = 0; i < n; i++) peak = Math.max(peak, Math.abs(d[i]));
    for (let i = 0; i < n; i++) d[i] *= 0.8 / Math.max(1e-6, peak);
  }
  return buf;
}

/** Скрип кованых петель: «прилипание-скольжение» — щелчки с плавающей частотой. */
function creak(ctx: BaseAudioContext, dur: number, seed: number): AudioBuffer {
  const sr = ctx.sampleRate;
  const n = Math.round(dur * sr);
  const buf = ctx.createBuffer(1, n, sr);
  const d = buf.getChannelData(0);
  const r = rng(seed);
  let t = 0.02;
  const res = [
    [610, 0.006, 1],
    [1370, 0.004, 0.6],
    [2900, 0.002, 0.25],
  ];
  while (t < dur - 0.05) {
    const u = t / dur;
    const rate = 22 + 50 * Math.sin(u * Math.PI) + Math.sin(u * 17) * 8 + (r() - 0.5) * 6;
    const amp = Math.sin(u * Math.PI) ** 0.6 * (0.6 + r() * 0.4);
    const i0 = Math.round(t * sr);
    for (const [f, dec, a] of res) {
      for (let k = 0; k < sr * dec * 5 && i0 + k < n; k++) {
        d[i0 + k] += Math.sin((2 * Math.PI * f * k) / sr) * Math.exp(-k / (sr * dec)) * a * amp;
      }
    }
    t += 1 / Math.max(8, rate);
  }
  let peak = 0;
  for (let i = 0; i < n; i++) peak = Math.max(peak, Math.abs(d[i]));
  for (let i = 0; i < n; i++) d[i] *= 0.7 / Math.max(1e-6, peak);
  return buf;
}

/** Свист падающего логотипа: шум с поднимающимся фильтром, обрыв точно в удар. */
function whoosh(ctx: BaseAudioContext, dur: number): AudioBuffer {
  const sr = ctx.sampleRate;
  const n = Math.round(dur * sr);
  const buf = ctx.createBuffer(1, n, sr);
  const d = buf.getChannelData(0);
  const r = rng(5);
  let lp = 0;
  for (let i = 0; i < n; i++) {
    const u = i / n;
    const a = onePoleCoef(300 + 5000 * u * u, sr);
    lp += a * (r() * 2 - 1 - lp);
    d[i] = lp * u * u * 1.6;
  }
  return buf;
}

// ─── сборка сцены звука ──────────────────────────────────────────────────

export interface SoundBank {
  music: AudioBuffer | null;
  near: AudioBuffer;
  huge: AudioBuffer;
  mid: AudioBuffer;
  far: AudioBuffer;
  rain: AudioBuffer;
  wind: AudioBuffer;
  creak: AudioBuffer;
  whoosh: AudioBuffer;
}

export function synthBank(ctx: BaseAudioContext, music: AudioBuffer | null): SoundBank {
  return {
    music,
    near: thunder(ctx, { dur: 5, crack: 1, lp: 170, delay: 0, seed: 1, gain: 1 }),
    huge: thunder(ctx, { dur: 6, crack: 1, lp: 140, delay: 0, seed: 2, gain: 1 }),
    mid: thunder(ctx, { dur: 4.5, crack: 0.25, lp: 120, delay: 0.25, seed: 3, gain: 0.8 }),
    far: thunder(ctx, { dur: 4, crack: 0, lp: 85, delay: 0.5, seed: 4, gain: 0.6 }),
    rain: noiseLoop(ctx, 6, 41, "rain"),
    wind: noiseLoop(ctx, 8, 42, "wind"),
    creak: creak(ctx, 2.2, 9),
    whoosh: whoosh(ctx, 0.5),
  };
}

/**
 * Расставить всё по времени. start — время контекста, соответствующее t=0
 * заставки. Возвращает узел общей громкости (для затухания).
 */
export function schedule(
  ctx: BaseAudioContext,
  bank: SoundBank,
  start: number,
  out: AudioNode,
  vol: { music: number; sound: number },
  /** Только одна группа (для подбора громкостей по отдельным дорожкам). */
  only?: "music" | "amb" | "thunder" | "fx",
): GainNode {
  const on = (tag: string): boolean => !only || only === tag;
  const master = ctx.createGain();
  master.gain.value = 1;
  // компрессор: гром и удар музыки не должны клиппировать в сумме
  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = -10;
  comp.knee.value = 8;
  comp.ratio.value = 6;
  comp.attack.value = 0.003;
  comp.release.value = 0.25;
  master.connect(comp);
  comp.connect(out);

  const musicBus = ctx.createGain();
  musicBus.gain.value = Math.min(1, vol.music * 1.3);
  musicBus.connect(master);
  const sfx = ctx.createGain();
  sfx.gain.value = vol.sound * 0.95;
  sfx.connect(master);

  const play = (b: AudioBuffer, at: number, gain: number, opts: { loop?: boolean; pan?: number; rate?: number; fadeIn?: number } = {}): GainNode => {
    const src = ctx.createBufferSource();
    src.buffer = b;
    src.loop = !!opts.loop;
    if (opts.rate) src.playbackRate.value = opts.rate;
    const gn = ctx.createGain();
    if (opts.fadeIn) {
      gn.gain.setValueAtTime(0, start + at);
      gn.gain.linearRampToValueAtTime(gain, start + at + opts.fadeIn);
    } else {
      gn.gain.value = gain;
    }
    let tail: AudioNode = gn;
    if (opts.pan !== undefined && typeof ctx.createStereoPanner === "function") {
      const p = ctx.createStereoPanner();
      p.pan.value = opts.pan;
      gn.connect(p);
      tail = p;
    }
    src.connect(gn);
    tail.connect(sfx);
    src.start(start + Math.max(0, at));
    return gn;
  };

  if (bank.music && on("music")) {
    const m = ctx.createBufferSource();
    m.buffer = bank.music;
    m.connect(musicBus);
    m.start(start);
  }

  // фон: дождь и ветер всю заставку
  if (on("amb")) {
    play(bank.rain, 0, 0.16, { loop: true, fadeIn: 1.6 });
    play(bank.wind, 0, 0.13, { loop: true, fadeIn: 2.5 });
  }

  // гром к каждой молнии: близкие — сразу, дальние — с задержкой звука
  for (const s of STRIKES) {
    if (!on("thunder")) break;
    // предвестник перед первым ударом — без звука, чтобы не смазать удар
    if (s.t !== CUE.hitA && Math.abs(s.t - CUE.hitA) < 0.3) continue;
    if (s.t === CUE.hitA) {
      play(bank.near, s.t - 0.01, 1, { pan: 0.1 });
    } else if (s.t === CUE.hitB) {
      play(bank.huge, s.t - 0.01, 1, { pan: -0.15 });
    } else if (s.t > CUE.hitB) {
      if (s.t < CUE.hitB + 0.1) play(bank.near, s.t, 0.5, { pan: 0.4, rate: 0.9 });
      else play(bank.mid, s.t + 0.2, 0.55, { pan: 0.5 });
    } else if (s.power >= 0.45) {
      play(bank.mid, s.t + 0.25, 0.5, { pan: -0.5 });
    } else if (s.power >= 0.2) {
      play(bank.far, s.t + 0.5, 0.35, { pan: s.seed % 2 ? 0.6 : -0.6 });
    }
  }

  // карканье стаи после первого удара
  const caw = (at: number, pan: number, pitch: number): void => {
    const t0 = start + at;
    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(410 * pitch, t0);
    osc.frequency.exponentialRampToValueAtTime(300 * pitch, t0 + 0.3);
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 34;
    const lfoG = ctx.createGain();
    lfoG.gain.value = 26 * pitch;
    lfo.connect(lfoG);
    lfoG.connect(osc.frequency);
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 1250;
    bp.Q.value = 2.2;
    const bp2 = ctx.createBiquadFilter();
    bp2.type = "peaking";
    bp2.frequency.value = 2500;
    bp2.gain.value = 9;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(0.32, t0 + 0.02);
    g.gain.setValueAtTime(0.3, t0 + 0.22);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.34);
    osc.connect(bp);
    bp.connect(bp2);
    bp2.connect(g);
    let tail: AudioNode = g;
    if (typeof ctx.createStereoPanner === "function") {
      const p = ctx.createStereoPanner();
      p.pan.value = pan;
      g.connect(p);
      tail = p;
    }
    tail.connect(sfx);
    osc.start(t0);
    lfo.start(t0);
    osc.stop(t0 + 0.4);
    lfo.stop(t0 + 0.4);
  };
  const cr = rng(17);
  const cawTimes = [0.32, 0.62, 0.78, 1.2, 1.55, 2.1, 2.45];
  if (on("fx")) cawTimes.forEach((dt, i) => caw(CUE.hitA + dt, (cr() - 0.5) * 1.6, 0.9 + cr() * 0.25 - (i % 3) * 0.04));

  // скрип створок
  if (on("fx")) {
    play(bank.creak, CUE.gateOpenStart, 0.55, { pan: -0.25 });
    play(bank.creak, CUE.gateOpenStart + 0.5, 0.4, { pan: 0.3, rate: 0.82 });
  }

  // удар логотипа: свист → суб-бас + шумовой удар
  if (on("fx")) play(bank.whoosh, CUE.hitB - 0.5, 0.55);
  const t0 = start + CUE.hitB;
  const sub = ctx.createOscillator();
  sub.type = "sine";
  sub.frequency.setValueAtTime(64, t0);
  sub.frequency.exponentialRampToValueAtTime(27, t0 + 1.1);
  const subG = ctx.createGain();
  subG.gain.setValueAtTime(0, t0);
  subG.gain.linearRampToValueAtTime(0.95, t0 + 0.008);
  subG.gain.exponentialRampToValueAtTime(0.001, t0 + 1.6);
  sub.connect(subG);
  subG.connect(sfx);
  if (on("fx")) {
    sub.start(t0);
    sub.stop(t0 + 1.7);
  }

  // общий спад к меню
  master.gain.setValueAtTime(1, start + CUE.fadeOutStart);
  master.gain.linearRampToValueAtTime(0, start + CUE.end + 0.4);
  return master;
}

// ─── живое воспроизведение ───────────────────────────────────────────────

export class IntroSound {
  private ctx: AudioContext | null = null;
  private musicBytes: Promise<ArrayBuffer | null> | null = null;
  private bank: SoundBank | null = null;
  private master: GainNode | null = null;
  private t0 = 0;

  /** Начать скачивать музыку заранее (без жеста можно). */
  prefetch(): void {
    if (!this.musicBytes) {
      this.musicBytes = fetch(musicUrl)
        .then((r) => (r.ok ? r.arrayBuffer() : null))
        .catch(() => null);
    }
  }

  /** Вызывать СТРОГО внутри обработчика жеста: создаёт и будит AudioContext. */
  unlock(): void {
    if (this.ctx) {
      void this.ctx.resume().catch(() => {});
      return;
    }
    try {
      const AC: typeof AudioContext | undefined =
        window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return;
      this.ctx = new AC(); // без опций: старый webkitAudioContext их не принимает
      // тихий буфер — разблокировка для iOS
      const b = this.ctx.createBuffer(1, 1, 22050);
      const s = this.ctx.createBufferSource();
      s.buffer = b;
      s.connect(this.ctx.destination);
      s.start(0);
      void this.ctx.resume().catch(() => {});
    } catch {
      this.ctx = null;
    }
  }

  /** Декодировать музыку и синтезировать звуки. false — звука не будет. */
  async prepare(): Promise<boolean> {
    if (!this.ctx) return false;
    if (this.bank) return true;
    this.prefetch();
    // resume() из жеста завершается не сразу; ждём, но не дольше 0.6 с
    await Promise.race([this.ctx.resume().catch(() => {}), new Promise((r) => setTimeout(r, 600))]);
    let music: AudioBuffer | null = null;
    try {
      const bytes = await this.musicBytes;
      if (bytes) {
        // slice: decodeAudioData «забирает» буфер, а он может понадобиться повторно
        music = await this.ctx.decodeAudioData(bytes.slice(0));
      }
    } catch {
      music = null;
    }
    this.bank = synthBank(this.ctx, music);
    return true;
  }

  get running(): boolean {
    return !!this.ctx && this.ctx.state === "running";
  }

  /** Запустить; вернуть true, если часы заставки идут от звука. */
  start(): boolean {
    if (!this.ctx || !this.bank) return false;
    this.t0 = this.ctx.currentTime + 0.06;
    this.master = schedule(this.ctx, this.bank, this.t0, this.ctx.destination, {
      music: readVol(LS_MUSIC, 0.7),
      sound: readVol(LS_SOUND, 1),
    });
    return this.ctx.state === "running";
  }

  /** Время заставки по часам звука. */
  now(): number {
    return this.ctx ? this.ctx.currentTime - this.t0 : 0;
  }

  fadeOut(sec: number): void {
    if (!this.ctx || !this.master) return;
    const g = this.master.gain;
    const now = this.ctx.currentTime;
    g.cancelScheduledValues(now);
    g.setValueAtTime(g.value, now);
    g.linearRampToValueAtTime(0, now + sec);
  }

  close(): void {
    const c = this.ctx;
    this.ctx = null;
    this.bank = null;
    this.master = null;
    if (c) void c.close().catch(() => {});
  }
}

/** Отрисовать всю звуковую дорожку офлайн (обзорный ролик). */
export async function renderSoundtrack(sampleRate = 44100, only?: "music" | "amb" | "thunder" | "fx"): Promise<AudioBuffer> {
  const len = CUE.end + 1.2;
  const off = new OfflineAudioContext(2, Math.ceil(len * sampleRate), sampleRate);
  const bytes = await fetch(musicUrl).then((r) => r.arrayBuffer());
  const music = await off.decodeAudioData(bytes);
  const bank = synthBank(off, music);
  schedule(off, bank, 0, off.destination, { music: 0.7, sound: 1 }, only);
  return off.startRendering();
}
